/**
 * TeamsPage — Administration › Teams (= Funktionsgruppen admin, Story UI-U6 AC5).
 *
 * Table (Name, Beschreibung, Mitglieder avatar-stack, Bearbeiten) + create/edit
 * dialog with a member checkbox-picker (member list = GET /admin/members). Wired
 * to the EXISTING funktionsgruppen endpoints. HONEST OMISSIONS: no team color, no
 * "Aktive Aufgaben" count (no endpoints).
 *
 * WS4 (Berechtigungskonzept V3.0 Bereich 4 / UC8): the edit dialog additionally
 * offers a "befristete Stellvertretung" mini-form — add a member with a von/bis
 * date range, calling POST .../members with the optional window straight through
 * (separate from the checkbox-diff bulk save, which stays permanent-only). Members
 * carrying a window show a "befristet bis …" pill. Only on an EXISTING team (needs
 * a real groupId to call immediately) — creating a NEW team's temp members is an
 * honest omission (add the team, then add the Stellvertretung).
 */

import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AdminMemberDto } from '@brokly/shared';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { AdminPageHeader } from '../AdminPageHeader';
import { ADMIN_PAGE_META } from '../adminNav';
import { useAdminMembers, useFunktionsgruppen, useFunktionsgruppe } from '../hooks';
import { useEffectivePermissions } from '../useEffectivePermissions';
import {
  Button,
  Dialog,
  LoadingState,
  ErrorState,
  EmptyState,
  TextInput,
} from '../components';
import type { FunktionsgruppeDto, FunktionsgruppeDetailDto, FunktionsgruppeMemberDto } from '../types';

const meta = ADMIN_PAGE_META.teams;

// ── Stellvertretung window helpers (Berechtigungskonzept V3.0 Bereich 4 / UC8, WS4) ──

/** `<input type="date">` yields YYYY-MM-DD; widen to a UTC instant for @IsISO8601 (mirrors NeueAufgabeDialog). */
function startOfDayIso(dateStr: string): string {
  return new Date(`${dateStr}T00:00:00.000Z`).toISOString();
}

/**
 * "Bis" is the day the Stellvertretung should STILL be active on — the api's
 * gueltig_bis is the EXCLUSIVE end, so a chosen calendar day maps to the START
 * of the FOLLOWING day (day-exact revocation: active through 23:59:59 of the
 * chosen day, gone from midnight on).
 */
function endOfDayExclusiveIso(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString();
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Small "befristet bis DD.MM.YYYY" pill for a temporary member row. */
function StellvertretungBadge({ member }: { member: FunktionsgruppeMemberDto | undefined }) {
  if (member?.gueltig_bis == null) return null;
  return (
    <span className="ml-auto shrink-0 rounded-full bg-status-warning-bg px-1.5 py-0.5 text-[10px] font-medium text-status-warning">
      befristet bis {formatDate(member.gueltig_bis)}
    </span>
  );
}

/**
 * Add-a-temporary-member mini-form (WS4). Calls addFunktionsgruppeMember
 * directly with a von/bis window — separate from the checkbox-diff bulk save
 * above (that path stays permanent-only), and only shown on an EXISTING team
 * (needs a real groupId to call immediately).
 */
function TempMemberForm({
  groupId,
  candidates,
  onAdded,
}: {
  groupId: string;
  candidates: AdminMemberDto[];
  onAdded: (userId: string) => void;
}) {
  const [userId, setUserId] = useState('');
  const [von, setVon] = useState('');
  const [bis, setBis] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () =>
      apiClient.admin.addFunktionsgruppeMember(groupId, userId, {
        ...(von ? { gueltig_von: startOfDayIso(von) } : {}),
        ...(bis ? { gueltig_bis: endOfDayExclusiveIso(bis) } : {}),
      }),
    onSuccess: () => {
      onAdded(userId);
      setUserId('');
      setVon('');
      setBis('');
      setError(null);
    },
    onError: (e: Error) => setError(e.message),
  });

  if (candidates.length === 0) return null;

  return (
    <div className="flex flex-wrap items-end gap-2">
      <div className="flex min-w-40 flex-1 flex-col gap-1">
        <label htmlFor="stlv-user" className="text-[11px] text-muted-foreground">
          Person
        </label>
        <select
          id="stlv-user"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          className="h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground"
        >
          <option value="">Auswählen…</option>
          {candidates.map((c) => (
            <option key={c.user_id} value={c.user_id}>
              {c.display_name ?? c.email}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor="stlv-von" className="text-[11px] text-muted-foreground">
          Von
        </label>
        <input
          id="stlv-von"
          type="date"
          value={von}
          onChange={(e) => setVon(e.target.value)}
          className="h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground"
        />
      </div>
      <div className="flex flex-col gap-1">
        <label htmlFor="stlv-bis" className="text-[11px] text-muted-foreground">
          Bis
        </label>
        <input
          id="stlv-bis"
          type="date"
          value={bis}
          onChange={(e) => setBis(e.target.value)}
          className="h-9 rounded-md border border-border bg-background px-2 text-sm text-foreground"
        />
      </div>
      <Button
        variant="outline"
        size="sm"
        disabled={!userId || add.isPending}
        onClick={() => add.mutate()}
      >
        Hinzufügen
      </Button>
      {error != null && <div className="w-full text-xs text-status-danger">{error}</div>}
    </div>
  );
}

function memberInitials(m: { display_name: string | null; user_id: string }): string {
  const src = m.display_name ?? m.user_id;
  return src.slice(0, 2).toUpperCase();
}

function AvatarStack({ members }: { members: FunktionsgruppeDetailDto['members'] }) {
  if (members.length === 0) return <span className="text-xs text-muted-foreground">-</span>;
  return (
    <div className="flex items-center">
      {members.slice(0, 4).map((m, i) => (
        <span
          key={m.user_id}
          className="grid size-6 place-items-center rounded-full border border-card bg-primary/15 text-[10px] font-semibold text-primary"
          style={{ marginLeft: i === 0 ? 0 : -6 }}
          title={m.display_name ?? m.user_id}
        >
          {memberInitials(m)}
        </span>
      ))}
      {members.length > 4 && (
        <span className="ml-1 text-xs tabular-nums text-muted-foreground">
          +{members.length - 4}
        </span>
      )}
    </div>
  );
}

// ── Team create/edit dialog ─────────────────────────────────────────────────
function TeamDialog({
  team,
  members,
  onClose,
}: {
  team: FunktionsgruppeDto | null;
  members: AdminMemberDto[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isEdit = team != null;
  const [name, setName] = useState(team?.name ?? '');
  const [description, setDescription] = useState(team?.description ?? '');
  const [error, setError] = useState<string | null>(null);

  // Existing members of the team (edit only) — the mock-aware hook, gated on edit.
  const detailQuery = useFunktionsgruppe(team?.id ?? '', isEdit);
  const existingMemberIds = new Set(
    (detailQuery.data?.members ?? []).map((m) => m.user_id),
  );
  // WS4: user_id → membership row, so the checkbox list can show a "befristet bis …" pill.
  const windowByUserId = new Map<string, FunktionsgruppeMemberDto>(
    (detailQuery.data?.members ?? []).map((m) => [m.user_id, m]),
  );

  const [selected, setSelected] = useState<Set<string> | null>(null);
  const effectiveSelected = selected ?? existingMemberIds;
  // WS4: who is left to offer a befristete Stellvertretung to.
  const tempCandidates = members.filter((m) => !effectiveSelected.has(m.user_id));

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.adminFunktionsgruppen() });
    if (isEdit) void qc.invalidateQueries({ queryKey: qk.adminFunktionsgruppe(team.id) });
  };

  const save = useMutation({
    mutationFn: async () => {
      let groupId = team?.id;
      if (isEdit) {
        await apiClient.admin.updateFunktionsgruppe(team.id, { name, description });
      } else {
        const created = await apiClient.admin.createFunktionsgruppe({ name, description });
        groupId = created.id;
      }
      if (groupId == null) return;
      // Diff membership (idempotent per-member calls).
      const target = effectiveSelected;
      for (const m of members) {
        const want = target.has(m.user_id);
        const have = existingMemberIds.has(m.user_id);
        if (want && !have) await apiClient.admin.addFunktionsgruppeMember(groupId, m.user_id);
        if (!want && have) await apiClient.admin.removeFunktionsgruppeMember(groupId, m.user_id);
      }
    },
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const del = useMutation({
    mutationFn: () => apiClient.admin.deleteFunktionsgruppe(team!.id),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (e: Error) => setError(e.message),
  });

  const toggle = (userId: string) =>
    setSelected(() => {
      const next = new Set(effectiveSelected);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });

  return (
    <Dialog
      open
      onClose={onClose}
      title={isEdit ? team.name : 'Team erstellen'}
      description="Funktionsgruppe für das Aufgaben-Routing."
      footer={
        <div className="flex flex-1 items-center justify-between">
          <div>
            {isEdit && (
              <Button
                variant="ghost"
                className="text-status-danger hover:bg-status-danger-bg"
                disabled={del.isPending}
                onClick={() => del.mutate()}
              >
                Löschen
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
            <Button disabled={save.isPending || !name.trim()} onClick={() => save.mutate()}>
              Speichern
            </Button>
          </div>
        </div>
      }
    >
      {error != null && <div className="mb-3 text-sm text-status-danger">{error}</div>}
      <div className="space-y-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Name</label>
          <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="Team" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Beschreibung
          </label>
          <TextInput value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Mitglieder
          </div>
          <ul className="max-h-64 space-y-1 overflow-y-auto rounded-md border border-border p-1">
            {members.map((m) => (
              <li key={m.user_id}>
                <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-secondary/60">
                  <input
                    type="checkbox"
                    aria-label={m.display_name ?? m.email}
                    checked={effectiveSelected.has(m.user_id)}
                    onChange={() => toggle(m.user_id)}
                    className="size-4"
                  />
                  <span className="truncate">{m.display_name ?? m.email}</span>
                  <StellvertretungBadge member={windowByUserId.get(m.user_id)} />
                </label>
              </li>
            ))}
          </ul>
        </div>
        {isEdit && tempCandidates.length > 0 && (
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Befristete Stellvertretung
            </div>
            <TempMemberForm
              groupId={team.id}
              candidates={tempCandidates}
              onAdded={(userId) => {
                setSelected(new Set([...effectiveSelected, userId]));
                invalidate();
              }}
            />
          </div>
        )}
      </div>
    </Dialog>
  );
}

// ── Row with lazy member detail (for the avatar-stack) ──────────────────────
function TeamRow({
  team,
  onEdit,
  canManage,
}: {
  team: FunktionsgruppeDto;
  onEdit: () => void;
  canManage: boolean;
}) {
  const detail = useFunktionsgruppe(team.id);

  return (
    <li className="flex items-center justify-between gap-4 rounded-xl border border-border bg-card px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold">{team.name}</div>
        {team.description != null && (
          <div className="truncate text-xs text-muted-foreground">{team.description}</div>
        )}
      </div>
      <div className="shrink-0">
        <AvatarStack members={detail.data?.members ?? []} />
      </div>
      <Button variant="outline" size="sm" onClick={onEdit}>
        {canManage ? 'Bearbeiten' : 'Ansehen'}
      </Button>
    </li>
  );
}

export function TeamsPage() {
  const { can } = useEffectivePermissions();
  const canManage = can('funktionsgruppe.manage');
  const groupsQuery = useFunktionsgruppen();
  const membersQuery = useAdminMembers();
  const [dialog, setDialog] = useState<{ team: FunktionsgruppeDto | null } | null>(null);

  const groups = useMemo(
    () => groupsQuery.data?.funktionsgruppen ?? [],
    [groupsQuery.data],
  );

  return (
    <div>
      <AdminPageHeader
        title={meta.title}
        description={meta.description}
        actions={
          canManage ? <Button onClick={() => setDialog({ team: null })}>Neues Team</Button> : undefined
        }
      />
      {groupsQuery.isLoading ? (
        <LoadingState />
      ) : groupsQuery.isError ? (
        <ErrorState />
      ) : groups.length === 0 ? (
        <EmptyState label="Noch keine Teams. Teams verteilen Aufgaben an Funktionsgruppen." />
      ) : (
        <ul className="space-y-2">
          {groups.map((g) => (
            <TeamRow
              key={g.id}
              team={g}
              canManage={canManage}
              onEdit={() => setDialog({ team: g })}
            />
          ))}
        </ul>
      )}
      {dialog != null && (
        <TeamDialog
          team={dialog.team}
          members={membersQuery.data?.members ?? []}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
}
